import React, { useMemo } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import type { View } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { CalendarToolbar } from '../components/CalendarToolbar';
import { HolidaysList } from '../components/HolidaysList';
import { LegendPanel, HOLIDAY_COLORS } from '../components/LegendPanel';
import { HolidayModal } from '../components/HolidayModal';
import { EventCard } from '../components/EventCard';
import { useCalendarLogic } from './logic';
import { cn } from '../../../utils/formatter';
import { Loading } from '../../../components';
import type { CalendarEvent } from '../hooks/useCalendarData';
import type { CalendarView } from '../hooks/useCalendarViews';
import type { Feriado } from '../../../models';
import './calendar.css';

// Configure date-fns localizer
const locales = {
  'pt-BR': ptBR
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek: () => startOfWeek(new Date(), { weekStartsOn: 0 }),
  getDay,
  locales
});

// Custom event styling
const eventStyleGetter = (event: CalendarEvent) => {
  const style: React.CSSProperties = {
    borderRadius: '4px',
    opacity: 0.8,
    border: '0',
    display: 'block',
    fontSize: '0.75rem',
    padding: '2px 4px'
  };

  if (event.type === 'holiday') {
    const feriado = event.data as Feriado;
    switch (feriado.tipo) {
      case 'NACIONAL':
        style.backgroundColor = '#ef4444';
        style.color = '#fff';
        break;
      case 'ESTADUAL':
        style.backgroundColor = '#008c3b';
        style.color = '#fff';
        break;
      case 'MUNICIPAL':
        style.backgroundColor = '#f19528';
        style.color = '#fff';
        break;
      case 'FOLGA':
        style.backgroundColor = '#2e00fa';
        style.color = '#fff';
        break;
    }
  } else if (event.type === 'demand') {
    style.backgroundColor = '#f97316';
    style.color = '#fff';
  }

  return { style };
};

export const CalendarUI: React.FC = () => {
  const {
    events,
    holidaysByType,
    counts,
    isLoading,
    canManageHolidays,
    view,
    currentDate,
    isHolidayModalOpen,
    selectedFeriado,
    showDemands,
    changeView,
    navigateNext,
    navigatePrev,
    navigateToday,
    handleEventClick,
    handleHolidayClick,
    handleEditHoliday,
    handleAddHoliday,
    handleModalClose,
    handleModalSuccess,
    handleShowMore,
    toggleShowDemands,
    getHolidaysForDate
  } = useCalendarLogic();

  // Map our custom view types to react-big-calendar views
  const calendarView = useMemo((): View => {
    if (view === 'day') return 'day';
    if (view === 'week') return 'week';
    return 'month'; // Default for month, quarter, semester, year
  }, [view]);

  // Filter events based on showDemands toggle
  const filteredEvents = useMemo(() => {
    if (showDemands) {
      return events; // Show all events
    }
    // Show only holidays
    return events.filter(event => event.type === 'holiday');
  }, [events, showDemands]);

  // For custom views (quarter, semester, year), we'll show month view
  // and handle the custom rendering separately
  const isCustomView = ['quarter', 'semester', 'year'].includes(view);

  return (
    <div className="h-[calc(100vh-6rem)] flex flex-col px-6 py-4">
      <div className="w-full flex flex-col h-full">
        {/* Toolbar */}
        <div className="flex-shrink-0">
            <CalendarToolbar
              view={view}
              currentDate={currentDate}
              onViewChange={changeView}
              onNavigatePrev={navigatePrev}
              onNavigateNext={navigateNext}
              onNavigateToday={navigateToday}
              {...(canManageHolidays && { onAddHoliday: handleAddHoliday })}
              isAdmin={canManageHolidays}
              showDemands={showDemands}
              onToggleShowDemands={toggleShowDemands}
            />
        </div>

        {/* Main Content */}
        <div className="flex-1 min-h-0 flex gap-4">
          {/* Calendar Area */}
          <div className="flex-1 min-w-0 bg-white dark:bg-neutral-800 rounded-lg border border-gray-200 dark:border-neutral-700 p-3 overflow-hidden flex flex-col">
            {isLoading ? (
              <div className="flex-1 flex items-center justify-center">
                <Loading message="Carregando calendário..." />
              </div>
            ) : isCustomView ? (
              // Custom view rendering for quarter, semester, and year
              <div className="flex-1 overflow-auto">
                <CustomCalendarView
                  view={view}
                  currentDate={currentDate}
                  events={filteredEvents}
                  onEventClick={handleEventClick}
                />
              </div>
            ) : (
              // Standard React Big Calendar
              <div className="flex-1 overflow-hidden calendar-container">
                <Calendar
                  localizer={localizer}
                  events={filteredEvents}
                  startAccessor="start"
                  endAccessor="end"
                  view={calendarView}
                  date={currentDate}
                  onNavigate={() => {}} // Controlled by our navigation
                  onView={() => {}} // Controlled by our view selector
                  toolbar={false} // We use custom toolbar
                  culture="pt-BR"
                  eventPropGetter={eventStyleGetter}
                  onSelectEvent={handleEventClick}
                  onShowMore={(_events, date) => {
                    handleShowMore(date);
                  }}
                  popup={false} // Disable default popup
                  doShowMoreDrillDown={false} // Don't drill down on click
                  // Show only 3 visible events per day, rest in "show more"
                  // Since holidays are hidden in month view (shown inline), this will show 3 demands
                  {...(view === 'month' && {
                    // This ensures React Big Calendar shows max 3 events before "show more"
                  })}
                  // Hide time slots for day and week views
                  min={new Date(1972, 0, 1, 0, 0, 0)}
                  max={new Date(1972, 0, 1, 23, 59, 59)}
                  timeslots={1}
                  step={1440} // 24 hours = full day
                  showMultiDayTimes={false}
                  allDayAccessor={() => true} // Treat all events as all-day
                  components={{
                    event: ({ event }) => (
                      <EventCard 
                        event={event as CalendarEvent} 
                        compact={view === 'month'}
                      />
                    ),
                    ...(view === 'month' && {
                      dateCellWrapper: ({ value, children }: any) => {
                        const holidays = getHolidaysForDate(value);
                        
                        // Get background color based on holiday type (if any holidays exist)
                        const getHolidayBackgroundColor = () => {
                          if (holidays.length === 0) return '';
                          
                          // If multiple holidays, use the first one's color
                          const mainHoliday = holidays[0];
                          const holidayColors = HOLIDAY_COLORS[mainHoliday.tipo as keyof typeof HOLIDAY_COLORS];
                          return holidayColors ? `${holidayColors.bg} ${holidayColors.border}` : '';
                        };

                        return (
                          <div className={`rbc-day-bg h-full flex flex-col overflow-hidden min-w-0 w-full ${getHolidayBackgroundColor()}`}>
                            {/* Holidays inline with date (date is rendered by default Big Calendar) */}
                            {holidays.length > 0 && (
                              <div className="flex flex-col gap-0.5 px-1 py-1 border-b border-gray-200 dark:border-neutral-600 min-h-0 overflow-hidden w-full min-w-0">
                                {holidays.map((holiday) => {
                                  const holidayColors = HOLIDAY_COLORS[holiday.tipo as keyof typeof HOLIDAY_COLORS];
                                  return (
                                    <div
                                      key={holiday.id}
                                      className={cn(
                                        'text-[10px] font-semibold px-1 py-0.5 rounded-sm bg-white/80 dark:bg-black/40',
                                        'overflow-hidden text-ellipsis whitespace-nowrap',
                                        'w-full min-w-0', // Garante truncamento no contexto flexbox
                                        holidayColors ? holidayColors.text : 'text-gray-700 dark:text-gray-400'
                                      )}
                                      title={`${holiday.descricao} (${holiday.tipo})`}
                                    >
                                      {holiday.descricao}
                                    </div>
                                  );
                                })}
                                {/* Indicador de mais feriados se houver mais de 2 */}
                                {holidays.length > 2 && (
                                  <div className="text-[10px] text-gray-500 dark:text-gray-400 px-1">
                                    +{holidays.length - 2} mais
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Events area */}
                            <div className="flex-1 p-1 overflow-hidden min-h-0">
                              {children}
                            </div>
                          </div>
                        );
                      },
                      month: {
                        event: ({ event }: any) => {
                          const calEvent = event as CalendarEvent;
                          // Only show demand cards in month view
                          if (calEvent.type === 'holiday') {
                            return null; // Holidays are shown inline with date
                          }
                          return <EventCard event={calEvent} compact={true} />;
                        }
                      }
                    })
                  }}
                  style={{ height: '100%' }}
                  messages={{
                    today: 'Hoje',
                    previous: 'Anterior',
                    next: 'Próximo',
                    month: 'Mês',
                    week: 'Semana',
                    day: 'Dia',
                    agenda: 'Agenda',
                    date: 'Data',
                    time: 'Hora',
                    event: 'Evento',
                    allDay: 'Dia inteiro',
                    noEventsInRange: 'Não há eventos neste período.',
                    showMore: (total) => `+ ${total} mais`
                  }}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="w-80 flex-shrink-0 flex flex-col gap-3 overflow-y-auto">
            {/* Holidays List */}
            <HolidaysList
              holidaysByType={holidaysByType}
              counts={counts}
              onHolidayClick={handleHolidayClick}
              onEditClick={handleEditHoliday}
              isAdmin={canManageHolidays}
            />

            {/* Legend */}
            <LegendPanel />
          </div>
        </div>
      </div>

      {/* Holiday Modal */}
      <HolidayModal
        isOpen={isHolidayModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        feriado={selectedFeriado}
      />
    </div>
  );
};

// Custom Calendar View Component for Quarter, Semester, and Year
interface CustomCalendarViewProps {
  view: CalendarView;
  currentDate: Date;
  events: CalendarEvent[];
  onEventClick: (event: CalendarEvent) => void;
}

const CustomCalendarView: React.FC<CustomCalendarViewProps> = ({
  view,
  currentDate,
  events,
  onEventClick
}) => {
  // Only render for custom views
  if (!['quarter', 'semester', 'year'].includes(view)) {
    return null;
  }
  const getMonthsToShow = () => {
    if (view === 'quarter') return 3;
    if (view === 'semester') return 6;
    return 12; // year
  };

  const monthsCount = getMonthsToShow();
  const gridCols = view === 'year' ? 'grid-cols-3' : view === 'semester' ? 'grid-cols-3' : 'grid-cols-3';

  return (
    <div className={cn('grid gap-4', gridCols)}>
      {Array.from({ length: monthsCount }).map((_, index) => {
        const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + index, 1);
        const monthEvents = events.filter(event => {
          const eventMonth = event.start.getMonth();
          const eventYear = event.start.getFullYear();
          return eventMonth === monthDate.getMonth() && eventYear === monthDate.getFullYear();
        });

        return (
          <div key={index} className="border border-gray-200 dark:border-neutral-700 rounded-lg p-3">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 text-center">
              {format(monthDate, 'MMMM yyyy', { locale: ptBR })}
            </h3>
            <div className="space-y-1">
              {monthEvents.length > 0 ? (
                monthEvents.slice(0, 5).map(event => (
                  <div
                    key={event.id}
                    onClick={() => onEventClick(event)}
                    className="cursor-pointer hover:opacity-80 transition-opacity"
                  >
                    <EventCard event={event} />
                  </div>
                ))
              ) : (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
                  Sem eventos
                </p>
              )}
              {monthEvents.length > 5 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  + {monthEvents.length - 5} mais
                </p>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

